/**
 * useKPIs Hooks
 * Provides cost KPI data from the server-side KPI module
 * Replaces client-side cost calculations in CostDashboardView
 */

import { useState, useEffect, useCallback } from 'react';
import { kpiAPI } from '../api.js';
import { REFERENCE_CURRENCY } from './useOrgCurrency.js';

/**
 * Hook for TCO (Total Cost of Ownership) breakdown
 * @param {string} period - 'month' | 'quarter' | 'year'
 * @returns {Object} { breakdown, grandTotal, avgPerVehicle, vehicleCount, fleetLossRatio, currency, totalRef, isLoading, error, refresh }
 */
export function useCostKPIs(period = 'month') {
  const [data, setData] = useState({
    breakdown: {
      fuel: 0,
      maintenance: 0,
      insurance: 0,
      leasing: 0,
      claims: 0,
      tax: 0,
      other: 0
    },
    grandTotal: 0,
    avgPerVehicle: 0,
    vehicleCount: 0,
    fleetLossRatio: null,
    currency: REFERENCE_CURRENCY,
    totalRef: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCosts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await kpiAPI.getCostBreakdown(period);
      if (response.ok) {
        setData({
          breakdown: response.data.breakdown || {},
          grandTotal: response.data.grandTotal || 0,
          avgPerVehicle: response.data.avgPerVehicle || 0,
          vehicleCount: response.data.vehicleCount || 0,
          fleetLossRatio: response.data.fleetLossRatio || null,
          // Group reporting contract: backend sends numbers + `currency` (default EUR)
          currency: response.data.currency || 'EUR',
          totalRef: response.data.total_ref ?? response.data.grandTotal ?? 0
        });
      } else {
        throw new Error(response.error?.message || 'Failed to load cost KPIs');
      }
    } catch (err) {
      console.error('Error fetching cost KPIs:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  return {
    ...data,
    isLoading,
    error,
    refresh: fetchCosts
  };
}

/**
 * Hook for per-vehicle cost breakdown
 * @param {string} period - 'month' | 'quarter' | 'year'
 * @returns {Object} { vehicles, isLoading, error, refresh }
 */
export function useCostsByVehicle(period = 'month') {
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCostsByVehicle = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await kpiAPI.getCostsByVehicle(period);
      if (response.ok) {
        setVehicles(response.data.vehicles || []);
      } else {
        throw new Error(response.error?.message || 'Failed to load vehicle costs');
      }
    } catch (err) {
      console.error('Error fetching vehicle costs:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchCostsByVehicle();
  }, [fetchCostsByVehicle]);

  return {
    vehicles,
    isLoading,
    error,
    refresh: fetchCostsByVehicle
  };
}

/**
 * Hook for per-organization (subsidiary) cost breakdown — group reporting.
 * Shape: docs/implementations/group/GROUP_REPORTING_CONTRACT.md (Lot 1).
 * @param {string} period - 'month' | 'quarter' | 'year'
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - skip the request (single-org users)
 * @returns {Object} { organizations, byCountry, consolidated, referenceCurrency, ratesPerEur, isLoading, error, refresh }
 */
export function useCostsByOrg(period = 'month', { enabled = true } = {}) {
  const [data, setData] = useState({
    organizations: [],
    byCountry: [],
    consolidated: null,
    referenceCurrency: 'EUR',
    ratesPerEur: {}
  });
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const fetchCostsByOrg = useCallback(async () => {
    if (!enabled) { setIsLoading(false); return; }
    setIsLoading(true);
    setError(null);
    try {
      const response = await kpiAPI.getCostsByOrg(period);
      if (response.ok) {
        const d = response.data || {};
        setData({
          organizations: d.organizations || [],
          byCountry: d.by_country || [],
          consolidated: d.consolidated || null,
          referenceCurrency: d.reference_currency || 'EUR',
          ratesPerEur: d.rates_per_eur || {}
        });
      } else {
        throw new Error(response.error?.message || 'Failed to load costs by organization');
      }
    } catch (err) {
      console.error('Error fetching costs by organization:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [period, enabled]);

  useEffect(() => {
    fetchCostsByOrg();
  }, [fetchCostsByOrg]);

  return {
    ...data,
    isLoading,
    error,
    refresh: fetchCostsByOrg
  };
}

/**
 * Hook for all KPIs (general purpose)
 * @returns {Object} { kpis, isLoading, error, refresh }
 */
export function useAllKPIs() {
  const [kpis, setKpis] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchKPIs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await kpiAPI.getAll();
      if (response.ok) {
        setKpis(response.data || {});
      } else {
        throw new Error(response.error?.message || 'Failed to load KPIs');
      }
    } catch (err) {
      console.error('Error fetching KPIs:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);

  return {
    kpis,
    isLoading,
    error,
    refresh: fetchKPIs
  };
}

const kpiHooks = { useCostKPIs, useCostsByVehicle, useCostsByOrg, useAllKPIs };

export default kpiHooks;
